import React from "react";
import { Page } from "../activities/AppPage";
import { API } from "../API";
import { VisitBroadcastId, VisitInstanceId } from "../messages/ObjectIds";
import FinishOnboardingBeforeStaffingModal from "./FinishOnboardingBeforeStaffingModal";
import { CaregiverEngagementModalProps } from "./CaregiverVisitEngagementModals/CaregiverEngagementModals";
import {
  CaregiverRequestsVisitRequest,
  FlexibleRequestParamsReq,
  Geo,
  NotInterestedReason,
} from "../messages";
import { FlexibleVisitDaysMap, FlexibleVisitDuration } from "./useFlexibleVisit";
import { translate } from "../translate";
import { STRINGS } from "../strings";
import { ActivityStateMixinState } from "../activities/ActivityStateMixin";
import { Visit } from "../system/AppState/AppVisitsState";
import {
  PatternRequestDetails,
  SinglesRequestDetails,
} from "./CaregiverVisitEngagementModals/MedflytRequestNonFlexibleVisitDetails";
import { HashSet } from "../utils/HashSet";
import { Day } from "../messages/VisitBroadcast";
import { DayOfWeek, LocalDate, LocalTime } from "@js-joda/core";
import { getLocationDisplayLocation } from "../utils/addressComponentUtils";
import { metersToMilesRounded } from "./VisitListComponents";
import { calcGeoDistance } from "./DistanceLabel";
import { AppState } from "../system/AppState";

interface Props {
  visitBroadcastId: VisitBroadcastId;
  mixin: ActivityStateMixinState;
  api: API;
  appState: AppState;
  visit: Visit.Available | null;
  isCaregiverOfMedflyt: boolean;
  caregiverLocation: Geo | null;
  onClickRequestMoreVisits: () => void;
  onRequestNavigate: (page: Page) => void;
  onClickGoBack: () => void;
}

const MIN_DISTANCE_FOR_FAR_VISIT_WARNING = 10;

export default function useAvailableVisitEngagement(props: Props) {
  const [activeModalProps, setActiveModalProps] =
    React.useState<CaregiverEngagementModalProps | null>(null);
  const [isOnboardingModalOpen, setOnboardingModalOpen] = React.useState(false);

  const closeOnboardingModal = () => {
    setOnboardingModalOpen(false);
  };

  const onboardingModalProps: React.ComponentProps<typeof FinishOnboardingBeforeStaffingModal> = {
    isOpen: isOnboardingModalOpen,
    onRequestClose: closeOnboardingModal,
    onClickFinishOnboarding: () => {
      closeOnboardingModal();
      props.onRequestNavigate({ type: "OnboardingActivity" });
    },
  };

  const closeModal = () => {
    setActiveModalProps(null);
  };

  const requestVisit = async (
    visitBroadcastId: VisitBroadcastId,
    visitRequest: CaregiverRequestsVisitRequest
  ) => {
    setActiveModalProps({ type: "Loading" });
    await props.api
      .setVisitAccepted(visitBroadcastId, visitRequest)
      .then(() => {
        setActiveModalProps({
          type: "VisitRequestedSuccess",
          api: props.api,
          appState: props.appState,
          onRequestNavigate: props.onRequestNavigate,
          onClickRequestMoreVisits: props.onClickGoBack,
        });
      })
      .catch((e) => {
        setActiveModalProps({
          type: "Error",
          actions: [],
          title: "Error in requesting this visit",
          message: e.message,
          onClickClose: () => closeModal(),
        });
      });
  };

  const handleClickRequestVisit = (
    requestedVisits: HashSet<VisitInstanceId>,
    checkedPattern: HashSet<string>
  ) => {
    let distanceFromCaregiver = 0;

    if (props.visit === null) {
      setActiveModalProps({
        type: "Error",
        actions: [],
        title: "Error in requesting this visit",
        onClickClose: () => closeModal(),
      });
    } else {
      const requestedInstances = requestedVisits.toArray();
      const visitDetails = props.visit.publicVisitBroadcastDetails;
      const visitApproximateAddress = getLocationDisplayLocation(visitDetails.location);
      let request: PatternRequestDetails | SinglesRequestDetails = {
        type: "Singles",
        address: visitApproximateAddress,
        visitRate: visitDetails.price,
        instances:
          visitDetails.type === "Singles"
            ? visitDetails.visitInstances
                .filter((instance) => requestedInstances.includes(instance.visitInstanceId))
                .map((instance) => ({
                  startDateTime: instance.date.startDateTime,
                  endDateTime: instance.date.endDateTime,
                }))
            : [],
      };

      if (visitDetails.type === "Pattern") {
        request = {
          type: "Pattern",
          address: visitApproximateAddress,
          visitRate: visitDetails.price,
          startDate: LocalDate.from(visitDetails.startTimeLocal),
          endDate: LocalDate.from(visitDetails.endTimeLocal),
          days: checkedPattern.toArray().map((day) => {
            const shift = visitDetails.weeklyPattern.daysOfWeek[day as Day]?.[0];
            return {
              day: DayOfWeek[day as Day],
              startTime: shift?.startAt ?? LocalTime.MIN,
              endTime: shift?.endAt ?? LocalTime.MIN,
            };
          }),
        };
      }

      if (props.caregiverLocation !== null) {
        distanceFromCaregiver = metersToMilesRounded(
          calcGeoDistance(visitDetails.location.location, props.caregiverLocation, "K") * 1000
        );
      }

      setActiveModalProps(
        props.isCaregiverOfMedflyt
          ? {
              type: "MedflytRequestVisit",
              isFarVisit: distanceFromCaregiver > MIN_DISTANCE_FOR_FAR_VISIT_WARNING,
              requestDetails: request,
              distanceFromCaregiver: distanceFromCaregiver,
              visitBroadcastId: props.visitBroadcastId,
              requestedInstancesIds: requestedInstances,
              onClickRequestVisit: requestVisit,
              onClickRequestFarVisit: handleFarVisitWarningForNotFlexibleRequest,
              onClickClose: () => closeModal(),
            }
          : {
              type: "RequestVisit",
              requestDetails: request,
              visitBroadcastId: props.visitBroadcastId,
              requestedInstancesIds: requestedInstances,
              onClickRequestVisit: requestVisit,
              onClickClose: () => closeModal(),
            }
      );
    }
  };

  const requestFlexibleVisit = async (visitRequest: FlexibleRequestParamsReq) => {
    setActiveModalProps({ type: "Loading" });

    await props.api
      .setFlexibleVisitAccepted(props.visitBroadcastId, visitRequest)
      .then(() => {
        setActiveModalProps({
          type: "VisitRequestedSuccess",
          api: props.api,
          appState: props.appState,
          onRequestNavigate: props.onRequestNavigate,
          onClickRequestMoreVisits: props.onClickGoBack,
        });
      })
      .catch((e) => {
        setActiveModalProps({
          type: "Error",
          onClickClose: () => closeModal(),
          actions: [],
          title: "Error in requesting this visit",
          message: e.message,
        });
      });
  };

  const handleClickRequestFlexible = (daysMap: FlexibleVisitDaysMap) => {
    const days = [...daysMap.entries()].flatMap(([day, entry]) => {
      const duration: FlexibleVisitDuration | undefined = entry.durations.find((d) => d.isSelected);

      if (duration === undefined || entry.isChecked === false) {
        return [];
      }

      return {
        day,
        startTime: entry.startTime,
        endTime: entry.endTime ?? entry.startTime.plusHours(duration.numberOfHours),
      };
    });

    if (props.visit === null || !props.isCaregiverOfMedflyt) {
      setActiveModalProps({
        type: "Error",
        onClickClose: () => closeModal(),
        actions: [],
        title: "Error in requesting this visit",
      });
    } else {
      const visitDetails = props.visit.publicVisitBroadcastDetails;

      let distanceFromCaregiver = 0;
      if (props.caregiverLocation !== null) {
        distanceFromCaregiver = metersToMilesRounded(
          calcGeoDistance(
            props.visit.publicVisitBroadcastDetails.location.location,
            props.caregiverLocation,
            "K"
          ) * 1000
        );
      }

      setActiveModalProps({
        type: "MedflytRequestFlexibleVisit",
        requestDetails: {
          type: "Flexible",
          days,
          address: getLocationDisplayLocation(visitDetails.location),
          visitRate: visitDetails.price,
          endDate: LocalDate.from(visitDetails.endTimeLocal),
          startDate: LocalDate.from(visitDetails.startTimeLocal),
        },
        distanceFromCaregiver,
        isFarVisit: distanceFromCaregiver > MIN_DISTANCE_FOR_FAR_VISIT_WARNING,
        onClickRequestFarVisit: handleFarVisitWarningForFlexibleRequest,
        onClickRequestVisit: requestFlexibleVisit,
        onClickClose: () => {
          props.appState.refreshVisits();
          closeModal();
        },
      });
    }
  };

  const handleFarVisitWarningForFlexibleRequest = (
    distance: number,
    visitRequest: FlexibleRequestParamsReq
  ) => {
    setActiveModalProps({
      type: "FarVisitWarning",
      address:
        props.visit === null
          ? ""
          : getLocationDisplayLocation(props.visit.publicVisitBroadcastDetails.location),
      milesFromCaregivers: distance,
      onClickClose: () => closeModal(),
      onClickConfirm: () => requestFlexibleVisit(visitRequest),
    });
  };

  const handleFarVisitWarningForNotFlexibleRequest = (
    distance: number,
    visitBroadcastId: VisitBroadcastId,
    visitRequest: CaregiverRequestsVisitRequest
  ) => {
    setActiveModalProps({
      type: "FarVisitWarning",
      address:
        props.visit === null
          ? ""
          : getLocationDisplayLocation(props.visit.publicVisitBroadcastDetails.location),
      milesFromCaregivers: distance,
      onClickClose: () => closeModal(),
      onClickConfirm: () => requestVisit(visitBroadcastId, visitRequest),
    });
  };

  const rejectVisit = async (notInterestedReason: NotInterestedReason) => {
    setActiveModalProps({ type: "Loading" });

    await props.api
      .putNotInterested(props.visitBroadcastId, notInterestedReason)
      .then(() => {
        props.mixin.notificationsState.triggerNotificationsRefresh();
        props.onRequestNavigate({
          type: "Main",
          defaultNavigation: {
            type: "Visits",
            secondaryTab: "Available",
          },
        });
      })
      .catch((e) => {
        setActiveModalProps({
          type: "Error",
          onClickClose: () => closeModal(),
          actions: [],
          title: "Error in rejecting this visit",
          message: e.message,
        });
      });
  };

  const handleClickReject = () => {
    setActiveModalProps({
      type: "RejectVisit",
      onClickRejectVisit: rejectVisit,
      onClickClose: () => closeModal(),
    });
  };

  const cancelVisit = async (visitInstances: VisitInstanceId[]) => {
    setActiveModalProps({ type: "Loading" });

    await props.api
      .cancelVisitRequest(props.visitBroadcastId, visitInstances)
      .then(() => {
        props.onClickGoBack();
      })
      .catch((err: any) => {
        let message: string = "";

        if (err.statusCode === 400) {
          message = translate(STRINGS.CONTACT_AGENCY_FOR_MORE_INFO);
        }

        setActiveModalProps({
          type: "Error",
          onClickClose: () => closeModal(),
          actions: [],
          title: translate(STRINGS.CANT_CANCEL_AT_THIS_TIME),
          message,
        });
      });
  };

  const handleClickCancel = (requestedInstances: VisitInstanceId[]) => {
    setActiveModalProps({
      type: "CancelVisit",
      requestedInstances,
      onClickCancelVisit: cancelVisit,
      onClickClose: () => closeModal(),
    });
  };

  return {
    handleClickRequestVisit,
    handleClickRequestFlexible,
    handleClickReject,
    handleClickCancel,
    closeEngagementModal: closeModal,
    activeModalProps,
    onboardingModalProps: onboardingModalProps,
  };
}
